import { useQuery } from "@tanstack/react-query";
import { Download, RefreshCw, Smartphone } from "lucide-react";
import QRCode from "react-qr-code";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate, formatSize } from "@/components/desktop-app/WindowsInstallSection";
import { isAndroid, isIOS } from "@/lib/platform";

/**
 * Android 앱(APK) 설치 카드 — /desktop/android-latest.json (APK 와 같은 폴더, APK 배포 때 함께 갱신).
 * 스토어 배포 전까지는 APK 직접 설치 방식이라 "출처를 알 수 없는 앱" 허용이 필요하다.
 * 설치 페이지·설치 안내 모달·/chat 게이트(Android 강제 대상)가 함께 쓴다. Android 가 아닌 기기에서는 QR 을 함께 보여 준다.
 */
export interface AndroidLatest {
  version: string;
  version_code?: number;
  url: string;
  size?: number | null;
  built_at?: string | null;
  signing?: string | null;
}

export async function fetchAndroidLatest(): Promise<AndroidLatest> {
  const res = await fetch(`/desktop/android-latest.json?t=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`android-latest.json ${res.status}`);
  const data = (await res.json()) as AndroidLatest;
  if (!data?.version || !data?.url) throw new Error("android-latest.json 형식을 읽을 수 없습니다");
  return data;
}

export function useAndroidAppLatest(enabled = true) {
  return useQuery({ queryKey: ["android-app-latest"], queryFn: fetchAndroidLatest, staleTime: 60_000, retry: 1, enabled });
}

interface AndroidInstallSectionProps {
  highlight?: boolean;
  compact?: boolean;
}

export default function AndroidInstallSection({ highlight, compact = false }: AndroidInstallSectionProps) {
  const android = useAndroidAppLatest();
  const onAndroid = isAndroid();
  const onIOS = isIOS();
  const androidHref = android.data ? `/desktop/${android.data.url}` : undefined;
  const androidAbsoluteUrl =
    androidHref && typeof window !== "undefined" ? `${window.location.origin}${androidHref}` : undefined;
  const emphasize = highlight ?? onAndroid;

  return (
    <Card className={emphasize ? "border-primary/20" : undefined}>
      <CardHeader className="pb-3">
        <CardTitle className={`${compact ? "text-base" : "text-lg"} flex items-center gap-2`}>
          <Smartphone className="h-5 w-5 text-primary" />
          Android 앱 설치
        </CardTitle>
        <CardDescription>
          휴대폰용 HubCore 메신저입니다. 새 메시지가 앱 알림으로 오고, 웹과 같은 계정으로 로그인합니다.
          {onIOS ? " iPhone용 앱은 아직 제공하지 않습니다." : ""}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {android.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-10 w-48" />
          </div>
        ) : android.isError || !android.data ? (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-muted-foreground">아직 배포된 Android 앱이 없습니다.</p>
            <Button variant="outline" size="sm" onClick={() => android.refetch()}>
              <RefreshCw className="h-4 w-4 mr-1" /> 다시 확인
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-3">
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-base font-semibold">HubCore 메신저</span>
                  <Badge variant="secondary">v{android.data.version}</Badge>
                  <Badge variant="outline">Android</Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {android.data.url}
                  {android.data.size ? ` · ${formatSize(android.data.size)}` : ""}
                  {android.data.built_at ? ` · ${formatDate(android.data.built_at)} 배포` : ""}
                </p>
              </div>
              <Button asChild size="lg" className="shrink-0" disabled={onIOS}>
                <a href={androidHref} download={android.data.url}>
                  <Download className="h-4 w-4 mr-2" /> APK 다운로드
                </a>
              </Button>
              <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
                <li>받은 APK 파일을 열면 설치가 시작됩니다. "출처를 알 수 없는 앱" 허용을 물으면 이 브라우저(또는 파일 앱)에 허용합니다.</li>
                <li>Play 프로텍트가 "확인되지 않은 앱" 경고를 띄우면 <span className="font-medium text-foreground">무시하고 설치</span>를 누릅니다. 사내 배포용이라 스토어 서명이 없어 나오는 안내입니다.</li>
                <li>이미 설치돼 있으면 그 위에 덮어써 업데이트됩니다. 설치 후 웹과 같은 아이디·비밀번호로 로그인하세요.</li>
              </ol>
            </div>
            {!onAndroid && androidAbsoluteUrl && (
              <div className="flex shrink-0 flex-col items-center gap-2 self-center sm:self-start">
                <div className="rounded-md border bg-white p-2">
                  <QRCode value={androidAbsoluteUrl} size={128} />
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  휴대폰 카메라로 찍으면
                  <br />
                  바로 내려받습니다
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
